import { useContext, useMemo } from 'react'

import {
  getPassepartoutHoleSizeFromArtworkSize,
  getSizeDimensionsFromString,
  getSizeString,
} from '@lib/size'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderSelectedStateContext } from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { getHasPassepartout } from '@frame-builder/lib/passepartout'
import { getStateFrameSize } from '@frame-builder/lib/size'

import ProductDimensions from '@blocks/product/product-dimensions'

interface FrameDimensionsProps {
  title?: string
  isOpenByDefault?: boolean
  className?: string
}

const FrameDimensions = ({
  title,
  isOpenByDefault,
  className,
}: FrameDimensionsProps) => {
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)
  const { frameBuilderType } = useContext(FrameBuilderSettingsContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)

  const artworkSizeString = useMemo(() => {
    const artworkSizeString = getSizeString(
      selectedState.artwork.width ?? 0,
      selectedState.artwork.height ?? 0,
      {
        spacingAroundSeparator: true,
      },
    )

    return `${artworkSizeString} ${
      frameBuilderStrings.artworkSizeSizeSuffix ?? ''
    }`
  }, [frameBuilderStrings, selectedState])

  const innerFrameSizeString = useMemo(() => {
    if (frameBuilderType === 'passepartout') {
      return
    }

    // Sizes are stored in millimeters, shown in centimeters
    const frameRabbetWidth = (selectedState.profile?.rabbetWidth ?? 0) / 10

    // Subtract rabbet width from frame size
    const frameSize = getStateFrameSize(selectedState)
    const innerFrameWidth = frameSize[0] - frameRabbetWidth * 2
    const innerFrameHeight = frameSize[1] - frameRabbetWidth * 2
    const innerFrameSizeString = getSizeString(
      innerFrameWidth,
      innerFrameHeight,
      {
        spacingAroundSeparator: true,
      },
    )

    return `${innerFrameSizeString} ${
      frameBuilderStrings.artworkSizeSizeSuffix ?? ''
    }`
  }, [frameBuilderStrings, frameBuilderType, selectedState])

  const outerFrameSizeString = useMemo(() => {
    if (frameBuilderType === 'passepartout' || !innerFrameSizeString) {
      return
    }

    // Sizes are stored in millimeters, shown in centimeters
    const frameFaceWidth = (selectedState.profile?.width ?? 0) / 10

    // Add face width to inner frame size
    const [innerFrameWidth, innerFrameHeight] =
      getSizeDimensionsFromString(innerFrameSizeString)
    const outerFrameWidth = innerFrameWidth + frameFaceWidth * 2
    const outerFrameHeight = innerFrameHeight + frameFaceWidth * 2
    const outerFrameSizeString = getSizeString(
      outerFrameWidth,
      outerFrameHeight,
      {
        spacingAroundSeparator: true,
      },
    )

    return `${outerFrameSizeString} ${
      frameBuilderStrings.artworkSizeSizeSuffix ?? ''
    }`
  }, [
    frameBuilderStrings,
    frameBuilderType,
    innerFrameSizeString,
    selectedState,
  ])

  const passepartoutSizeString = useMemo(() => {
    const hasPassepartout = getHasPassepartout(selectedState.passepartout)

    if (!hasPassepartout) {
      return
    }

    const frameSize = getStateFrameSize(selectedState)
    const frameSizeString = getSizeString(frameSize[0], frameSize[1], {
      spacingAroundSeparator: true,
    })

    return `${frameSizeString} ${
      frameBuilderStrings.artworkSizeSizeSuffix ?? ''
    }`
  }, [frameBuilderStrings, selectedState])

  const passepartoutHoleSizeString = useMemo(() => {
    const hasPassepartout = getHasPassepartout(selectedState.passepartout)

    if (!hasPassepartout) {
      return
    }

    // Calculate mount cutout size
    const passepartoutHoleSize = getPassepartoutHoleSizeFromArtworkSize(
      selectedState.artwork.width ?? 0,
      selectedState.artwork.height ?? 0,
    )
    const passepartoutHoleSizeString = getSizeString(
      passepartoutHoleSize[0],
      passepartoutHoleSize[1],
      {
        spacingAroundSeparator: true,
      },
    )

    return `${passepartoutHoleSizeString} ${
      frameBuilderStrings.artworkSizeSizeSuffix ?? ''
    }`
  }, [frameBuilderStrings, selectedState])

  return (
    <ProductDimensions
      title={title}
      artworkSizeString={artworkSizeString}
      innerFrameSizeString={innerFrameSizeString}
      outerFrameSizeString={outerFrameSizeString}
      passepartoutSizeString={passepartoutSizeString}
      passepartoutHoleSizeString={passepartoutHoleSizeString}
      isOpenByDefault={isOpenByDefault}
      className={className}
    />
  )
}

export default FrameDimensions
